class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.debug = false;

    this.log('HeaderMenu: constructor called');
    this.header = document.querySelector('.header-wrapper');
    // Ensure header exists before proceeding
    if (!this.header) {
      this.log('HeaderMenu: header element not found');
      return;
    }
    this.initHoverEvents();
    this.log('HeaderMenu: initialized');
  }

  log(message, data = {}) {
    if (this.debug) {
      console.log(`[MegaMenuHover] ${message}`, data);
    }
  }

  connectedCallback() {
    this.log('HeaderMenu: connectedCallback called');
    // Ensure header exists before setting height
    if (!this.header) {
      this.log('HeaderMenu: header element not found in connectedCallback');
      return;
    }
    this.setHeaderHeight();
  }

  initHoverEvents() {
    this.log('HeaderMenu: initializing hover events');
    this.mainDetailsToggle.addEventListener('mouseenter', () => {
      this.log('HeaderMenu: mouseenter event on mainDetailsToggle');
      this.openMenu();
    });

    this.mainDetailsToggle.addEventListener('mouseleave', (event) => {
      this.log('HeaderMenu: mouseleave event on mainDetailsToggle', {
        relatedTarget: event.relatedTarget
      });
      // Check if the mouse is moving towards the mega menu content
      const megaMenuContent = this.mainDetailsToggle.querySelector('.mega-menu__content');
      if (megaMenuContent && event.relatedTarget === megaMenuContent || megaMenuContent?.contains(event.relatedTarget)) {
        // If moving to the content, add a listener to the content itself
        this.log('HeaderMenu: mouse moved to mega menu content');
        if (!megaMenuContent.hasAttribute('data-mouseleave-listener')) {
          megaMenuContent.addEventListener('mouseleave', this.closeMenu.bind(this), { once: true });
          megaMenuContent.setAttribute('data-mouseleave-listener', 'true');
          this.log('HeaderMenu: added mouseleave listener to mega menu content');
        }
        return;
      }
      // Close immediately if not hovering over the trigger or content
      this.log('HeaderMenu: mouse did not move to mega menu content, closing menu');
      this.closeMenu();
    });
  }

  openMenu() {
    this.log('HeaderMenu: opening menu');
    this.mainDetailsToggle.setAttribute('open', '');
    this.mainDetailsToggle.querySelector('summary')?.setAttribute('aria-expanded', 'true');
    // Use requestAnimationFrame to ensure styles are applied after 'open' attribute is set
    requestAnimationFrame(() => {
      this.header?.classList.add('header--mega-menu-open', 'color-scheme-1');
      this.mainDetailsToggle.querySelector('.mega-menu__content')?.style.setProperty('transform', 'translateY(0%)');
      // Animate menu items and image with staggered effect
      this.animateMenuItems();
      this.log('HeaderMenu: menu opened, styles applied');
    });
  }

  animateMenuItems() {
    this.log('HeaderMenu: animating menu content');
    
    // Reset any previous animations
    const content = this.mainDetailsToggle.querySelector('.mega-menu__content');
    if (!content) return;

    // First animate the image if it exists
    const imageWrapper = content.querySelector('.mega-menu__image-wrapper');
    if (imageWrapper) {
      const image = imageWrapper.querySelector('img');
      if (image) {
        // Add initial state
        image.style.opacity = '0';
        image.style.transform = 'scale(0.95)';
        
        // Trigger animation after a slight delay
        setTimeout(() => {
          image.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
          image.style.opacity = '1';
          image.style.transform = 'scale(1)';
        }, 500);
      }
    }

    // Then animate all menu links with a staggered effect
    const menuLinks = content.querySelectorAll('.mega-menu__link');
    this.log('HeaderMenu: found menu links for animation', { count: menuLinks.length });

    // Reset animations - add hidden class to all links
    menuLinks.forEach(link => {
      link.classList.add('menu-item-hidden');
      link.style.transitionDelay = '0s';
    });

    // Apply staggered animation with increasing delays
    menuLinks.forEach((link, index) => {
      setTimeout(() => {
        this.log('HeaderMenu: animating menu item', { index, text: link.textContent?.trim() });
        link.style.transitionDelay = `${index * 50}ms`;
        link.classList.remove('menu-item-hidden');
        link.classList.add('animate-menu-item');
      }, 650); // Base delay before starting animations
    });
  }

  closeMenu() {
     this.log('HeaderMenu: closing menu');
     const megaMenuContent = this.mainDetailsToggle.querySelector('.mega-menu__content');
     // Remove the temporary mouseleave listener if it exists
     megaMenuContent?.removeAttribute('data-mouseleave-listener');
     this.log('HeaderMenu: removed data-mouseleave-listener attribute');

    // Start the closing animation/transition
    megaMenuContent?.style.setProperty('transform', 'translateY(-100%)');
    this.header?.classList.remove('header--mega-menu-open', 'color-scheme-1');
    this.log('HeaderMenu: started closing animation');

    // Reset all animations when closing
    const menuLinks = megaMenuContent?.querySelectorAll('.mega-menu__link');
    if (menuLinks) {
      menuLinks.forEach(link => {
        link.classList.add('menu-item-hidden');
        link.classList.remove('animate-menu-item');
      });
    }

    // Use transitionend event to close the details element after the transition completes
    const closeDetails = () => {
      this.log('HeaderMenu: transitionend event fired');
      // Only close if the mouse hasn't re-entered the trigger or content area
      if (!this.mainDetailsToggle.matches(':hover') && !megaMenuContent?.matches(':hover')) {
          this.log('HeaderMenu: no hover detected, closing details element');
          this.mainDetailsToggle.removeAttribute('open');
          this.mainDetailsToggle.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      } else {
          this.log('HeaderMenu: hover detected, keeping menu open');
      }
      // Clean up the event listener
      megaMenuContent?.removeEventListener('transitionend', closeDetails);
      this.log('HeaderMenu: removed transitionend listener');
    };

    // Add the transitionend listener
    // Use { once: true } if you are sure no other transitions might trigger this prematurely
    // Otherwise, manually remove it as shown above.
    megaMenuContent?.addEventListener('transitionend', closeDetails);
    this.log('HeaderMenu: added transitionend listener');

    // Fallback timeout in case transitionend doesn't fire (e.g., element removed, display: none)
    // This might be overly cautious and could be removed if not needed.
    setTimeout(() => {
        this.log('HeaderMenu: fallback timeout triggered');
        if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.matches(':hover') && !megaMenuContent?.matches(':hover')) {
            console.warn('Mega menu transitionend fallback triggered.');
            this.mainDetailsToggle.removeAttribute('open');
            this.mainDetailsToggle.querySelector('summary')?.setAttribute('aria-expanded', 'false');
            megaMenuContent?.removeEventListener('transitionend', closeDetails); // Ensure cleanup
            this.log('HeaderMenu: fallback timeout closed the menu');
        } else {
            this.log('HeaderMenu: fallback timeout not closing menu, conditions not met');
        }
    }, 600); // Slightly longer than the expected transition duration
  }

  setHeaderHeight() {
    this.log('HeaderMenu: setting header height');
    // Debounce or throttle this if it causes performance issues on resize
    document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    this.log('HeaderMenu: header height set to', this.header.offsetHeight);
  }

  onToggle() {
    this.log('HeaderMenu: onToggle called');
    if (!this.header) {
      this.log('HeaderMenu: header element not found in onToggle');
      return;
    }
    // Use the 'open' attribute state to determine preventHide
    this.header.preventHide = this.mainDetailsToggle.hasAttribute('open');
    this.log('HeaderMenu: header.preventHide set to', this.header.preventHide);

    // Set header bottom position only if it hasn't been set
    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') === '') {
       this.log('HeaderMenu: setting header-bottom-position-desktop');
       // Use requestAnimationFrame to ensure layout is calculated correctly
       requestAnimationFrame(() => {
         const bottomPosition = Math.floor(this.header.getBoundingClientRect().bottom);
         document.documentElement.style.setProperty(
           '--header-bottom-position-desktop',
           `${bottomPosition}px`
         );
         this.log('HeaderMenu: header-bottom-position-desktop set to', bottomPosition);
       });
    } else {
      this.log('HeaderMenu: header-bottom-position-desktop already set');
    }
  }
}

customElements.define('header-menu', HeaderMenu);
